import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Filter, ChevronLeft, ChevronRight, X } from "lucide-react";
import PageShell from "@/components/PageShell";
import PageHeader from "@/components/PageHeader";
import SiteFooter from "@/components/SiteFooter";
import { PRODUCTS, CATEGORIES_LIST } from "@/data/products";
import { pickImage } from "@/data/imagePool";

const PAGE_SIZE = 12;

export default function Categories() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => setPage(1), [q, cat]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return PRODUCTS.filter((p) => {
      const matchCat = cat === "All" || p.category === cat;
      const matchQ = !term || p.name.toLowerCase().includes(term) || p.category.toLowerCase().includes(term);
      return matchCat && matchQ;
    });
  }, [q, cat]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <PageShell>
      <PageHeader
        overline="Catalog"
        title={<>73+ products,<br />ready to brand.</>}
        subtitle="Search across our full UAE catalog — signage, displays, brochure holders, banners, acrylic and more."
        breadcrumbs={[{ to: "/", label: "Home" }, { label: "Categories" }]}
      />

      <section className="py-12 md:py-16 bg-white dark:bg-[#0B1120] transition-colors">
        <div className="pz-container">
          {/* Controls */}
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between mb-8">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="catalog-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search for products… e.g. brochure, mug, flag"
                className="w-full h-12 pl-11 pr-10 rounded-full border border-slate-200 dark:border-white/15 bg-white dark:bg-white/5 text-sm text-[#0F172A] dark:text-white placeholder:text-slate-400 outline-none focus:border-[#0F172A] dark:focus:border-[#F59E0B] focus:ring-2 focus:ring-[#2563EB]/20"
              />
              {q && (
                <button
                  onClick={() => setQ("")}
                  data-testid="catalog-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-slate-100 dark:bg-white/10 grid place-items-center"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              <Filter className="w-4 h-4 text-slate-400 flex-shrink-0" />
              {["All", ...CATEGORIES_LIST].map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`catalog-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  className={`px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${
                    cat === c
                      ? "bg-[#0F172A] dark:bg-[#F59E0B] text-white dark:text-[#0F172A] border-transparent"
                      : "bg-white dark:bg-white/5 text-slate-600 dark:text-white/75 border-slate-200 dark:border-white/10 hover:border-[#0F172A] dark:hover:border-[#F59E0B]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="text-sm text-slate-500 dark:text-white/55 mb-6" data-testid="catalog-results-count">
            Showing {pageItems.length} of {filtered.length} products
          </div>

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} data-testid={`skeleton-${i}`} className="rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden">
                  <div className="h-48 bg-slate-100 dark:bg-white/5 animate-pulse" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-slate-100 dark:bg-white/5 rounded animate-pulse" />
                    <div className="h-3 w-2/3 bg-slate-100 dark:bg-white/5 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="catalog-empty" className="text-center py-20 rounded-2xl border border-slate-200 dark:border-white/10">
              <div className="text-2xl font-display text-[#0F172A] dark:text-white">No products matched your search.</div>
              <p className="mt-2 text-slate-500 dark:text-white/55 text-sm">Try a different keyword or clear filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {pageItems.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: (i % 10) * 0.03 }}
                  data-testid={`product-card-${p.slug}`}
                  className="group bg-white dark:bg-white/[0.03] border border-slate-100 dark:border-white/5 rounded-xl overflow-hidden hover:border-[#F59E0B]/30 transition-all duration-300"
                >
                  <Link to={`/categories/${p.slug}`} className="block">
                    <div className="aspect-square overflow-hidden bg-slate-50/50 dark:bg-white/5">
                      <img
                        src={pickImage(p.slug, p.name, 0, true)}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3">
                      <div className="text-[9px] uppercase tracking-wider text-slate-400 dark:text-white/40 font-medium">{p.category}</div>
                      <h3 className="mt-1 text-sm font-medium text-[#0F172A] dark:text-white line-clamp-2 leading-snug group-hover:text-[#2563EB] dark:group-hover:text-[#F59E0B] transition-colors">{p.name}</h3>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-2" data-testid="catalog-pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="page-prev"
                className="w-10 h-10 rounded-full border border-slate-200 dark:border-white/10 grid place-items-center disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-white/5"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  data-testid={`page-${i + 1}`}
                  className={`min-w-10 h-10 px-3 rounded-full text-sm font-medium ${
                    page === i + 1
                      ? "bg-[#0F172A] dark:bg-[#F59E0B] text-white dark:text-[#0F172A]"
                      : "border border-slate-200 dark:border-white/10 text-slate-700 dark:text-white/75 hover:bg-slate-50 dark:hover:bg-white/5"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="page-next"
                className="w-10 h-10 rounded-full border border-slate-200 dark:border-white/10 grid place-items-center disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-white/5"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </PageShell>
  );
}
